import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { noticeService } from '../../../services/noticeService';
import { Download, Mail, AlertCircle, FileText, CheckCircle, RefreshCw } from 'lucide-react';
import { Clock, Eye, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_DISPLAY_MAP_TEXT = {
  'Ciornă': { label: 'Ciornă', color: 'bg-gray-100 text-gray-600', Icon: FileText },
  'Generat': { label: 'Generat', color: 'bg-blue-100 text-blue-600', Icon: FileText },
  'Trimis': { label: 'Trimis', color: 'bg-indigo-100 text-indigo-600', Icon: Send },
  'Accesat': { label: 'Accesat', color: 'bg-purple-100 text-purple-600', Icon: Eye },
  'Semnat': { label: 'Semnat', color: 'bg-green-100 text-green-800', Icon: CheckCircle },
  'Expirat': { label: 'Expirat', color: 'bg-red-100 text-red-800', Icon: Clock },
  'Reziliat': { label: 'Reziliat', color: 'bg-red-100 text-red-800', Icon: AlertCircle },
  'Necunoscut': { label: 'Necunoscut', color: 'bg-yellow-100 text-yellow-600', Icon: AlertCircle },
};

const getNoticeStatusDisplay = (notice) => {
  const statusText = notice.statusText || 'Necunoscut';
  return STATUS_DISPLAY_MAP_TEXT[statusText] || STATUS_DISPLAY_MAP_TEXT['Necunoscut'];
};

const TerminationNoticeActions = ({ contractId, contractNumber, terminationNotices, formatDate, onNoticeChange, client }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [includeClientEmails, setIncludeClientEmails] = useState(true);
  const [article, setArticle] = useState('6.2');
  const [noticeDate, setNoticeDate] = useState('');
  const [message, setMessage] = useState(null);
  const [actionType, setActionType] = useState('create-new');
  const [noticeToActOn, setNoticeToActOn] = useState(null);

  const generatePdfMutation = useMutation(
    (noticeId) => noticeService.generatePdf(noticeId),
    {
      onSuccess: () => {
        if (onNoticeChange) onNoticeChange();
        toast.success('PDF generat cu succes');
      },
      onError: (error) => {
        console.error('PDF generation error:', error);
        toast.error('Eroare la generarea PDF: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleGeneratePdf = (noticeId) => {
    generatePdfMutation.mutate(noticeId);
  };

  const initiateFileDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const openModal = (type, notice = null) => {
    setActionType(type);
    setNoticeToActOn(notice);
    setEmailAddresses('');
    setIncludeClientEmails(true); // Bifat by default

    setArticle(notice?.article || '6.2');

    const today = new Date().toISOString().split('T')[0];
    setNoticeDate(notice?.date?.split('T')[0] || today);

    setShowModal(true);
    setMessage(null);
  };

  // Funcție helper pentru a obține email-urile finale
  const getFinalEmails = () => {
    // Parse manual emails
    const manualEmails = emailAddresses
      .split(/[,\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Collect client emails if checkbox is checked
    const clientEmails = [];
    if (includeClientEmails && client) {
      if (client.email) clientEmails.push(client.email);
      if (client.representativeEmail) clientEmails.push(client.representativeEmail);
    }

    // Combine and deduplicate
    const allEmails = [...new Set([...manualEmails, ...clientEmails])];
    return allEmails;
  };

  const handleCreateOrSendNotice = async (shouldSend) => {
    if (!article || !noticeDate) {
      setMessage({ type: 'error', text: 'Articolul și data notificării sunt obligatorii.' });
      return;
    }

    // Get final emails ONLY if sending
    const emails = shouldSend ? getFinalEmails() : [];

    // Validate emails if sending
    if (shouldSend && emails.length === 0) {
      setMessage({ 
        type: 'error', 
        text: 'Trebuie să specificați cel puțin o adresă de email pentru trimitere' 
      });
      return;
    }

    const createRequest = {
      contractId: contractId,
      date: noticeDate,
      terminationArticle: article,
      autoGeneratePdf: true,
      sendToClient: shouldSend,
      notificationEmails: emails,
    };

    try {
      setLoading(true);
      setMessage(null);

      console.log("create request:", createRequest);
      const newNotice = await noticeService.create(createRequest);

      let successMessage = `Notificarea de reziliere din ${formatDate(newNotice.date)} a fost generată cu succes!`;
      if (shouldSend) {
        successMessage += ` Email-ul a fost trimis către ${emails.length} destinatar${emails.length !== 1 ? 'i' : ''}.`;
      }

      if (typeof onNoticeChange === 'function') {
        onNoticeChange();
      }

      setShowModal(false);
      setMessage({ type: 'success', text: successMessage });

    } catch (error) {
      console.error('Eroare la procesarea notificării:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.response?.data?.Message || 'Eroare la procesarea notificării'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExisting = async (noticeId) => {
    try {
      setLoading(true);
      setMessage(null);

      const { blob, filename } = await noticeService.downloadPdf(noticeId);

      initiateFileDownload(blob, filename);

      setMessage({ type: 'success', text: `Notificarea a fost descărcată cu succes!` });
    } catch (error) {
      console.error('Eroare la descărcarea notificării:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.Message || 'Eroare la descărcarea PDF-ului notificării'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleSendExistingNotice = async (e) => {
    e.preventDefault();

    if (!noticeToActOn?.id) return;

    const emails = getFinalEmails();
    const requestBody = emails;

    try {
      setLoading(true);
      setMessage(null);

      const result = await noticeService.sendNotice(
        noticeToActOn.id,
        requestBody
      );

      if (typeof onNoticeChange === 'function') {
        onNoticeChange();
      }

      setMessage({
        type: 'success',
        text: `${result.message} (${result.recipients} destinatar${result.recipients > 1 ? 'i' : ''})`
      });

      setShowModal(false);
      setEmailAddresses('');
      setNoticeToActOn(null);
    } catch (error) {
      console.error('Eroare la trimiterea notificării:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Eroare la trimiterea notificării'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (actionType === 'create-new') {
      handleCreateOrSendNotice(false);
    } else if (actionType === 'send-new') {
      handleCreateOrSendNotice(true);
    } else if (actionType === 'send-existing') {
      handleSendExistingNotice(e);
    }
  };

  const getModalTitle = () => {
    switch (actionType) {
      case 'create-new':
        return 'Generează Notificare de Reziliere';
      case 'send-new':
        return 'Generează și Trimite Notificare de Reziliere';
      case 'send-existing':
        return `Trimite Notificare Existentă (${formatDate(noticeToActOn?.date)})`;
      default:
        return 'Notificare de Reziliere';
    }
  };

  const showNewNoticeDetails = actionType === 'create-new' || actionType === 'send-new';
  const showEmailField = actionType === 'send-new' || actionType === 'send-existing';

  // Helper pentru afișarea email-urilor clientului
  const getClientEmailsDisplay = () => {
    if (!client) return null;
    const emails = [];
    if (client.email) emails.push(client.email);
    if (client.representativeEmail) emails.push(client.representativeEmail);
    return emails.length > 0 ? emails.join(', ') : null;
  };

  const clientEmailsDisplay = getClientEmailsDisplay();

  return (
    <div className="termination-notice-actions">
      {message && (
        <div className={`alert alert-${message.type}`}>
          <AlertCircle size={16} />
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="action-buttons mb-4">
        <button
          onClick={() => openModal('create-new')}
          disabled={loading}
          className="btn btn-primary"
        >
          <FileText size={16} />
          Generează Notificare Nouă
        </button>

        <button
          onClick={() => openModal('send-new')}
          disabled={loading}
          className="btn btn-secondary"
        >
          <Mail size={16} />
          Generează și Trimite Notificare
        </button>
      </div>

      {terminationNotices && terminationNotices.length > 0 && (
        <div className="act-list-container">
          <h2 className="list-heading">
            Notificări de Reziliere Existente
          </h2>
          <div className="space-y-3">
            {terminationNotices.map((notice) => {
              const statusDisplay = getNoticeStatusDisplay(notice);
              const IconComponent = statusDisplay.Icon;

              return (
                <div key={notice.id} className="act-list-item">
                  <div>
                    <div className="act-number">
                      Notificare Reziliere
                    </div>
                    <div className="act-date">
                      {formatDate(notice.date)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`status-badge ${statusDisplay.color}`}>
                      <IconComponent className="w-3 h-3 mr-1" />
                      {statusDisplay.label}
                    </span>

                    {!notice.pdfFilePath ? (
                      <button
                        onClick={() => handleGeneratePdf(notice.id)}
                        disabled={generatePdfMutation.isLoading}
                        className="btn-link-primary"
                        title="Generează PDF"
                      >
                        <FileText size={16} />
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleGeneratePdf(notice.id)}
                          disabled={generatePdfMutation.isLoading}
                          className="btn-link"
                          title="Regenerează PDF"
                        >
                          <RefreshCw size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadExisting(notice.id)}
                          disabled={loading}
                          className="btn-link"
                          title="Descarcă PDF-ul Notificării"
                        >
                          <Download size={16} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => openModal('send-existing', notice)}
                      disabled={loading}
                      className="btn-link text-secondary-500"
                      title="Trimite Notificarea"
                    >
                      <Mail size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {getModalTitle()}
              </h3>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {showNewNoticeDetails && (
                  <>
                    <div className="form-group">
                      <label htmlFor="article">Articol Contract *</label>
                      <input
                        id="article"
                        type="text"
                        value={article}
                        onChange={(e) => setArticle(e.target.value)}
                        placeholder="6.2"
                        required
                        className="form-control"
                      />
                      <span className="hint">Articolul din contract care justifică rezilierea</span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="noticeDate">
                        Data Notificării *
                      </label>
                      <input
                        id="noticeDate"
                        type="date"
                        value={noticeDate}
                        onChange={(e) => setNoticeDate(e.target.value)}
                        required
                        className="form-control"
                      />
                      <span className="hint">Data la care este emisă notificarea</span>
                    </div>
                  </>
                )}

                {showEmailField && (
                  <>
                    <div className="form-group">
                      <label htmlFor="emailAddresses">
                        Adrese Email Adiționale
                        <span className="hint">
                          Adaugă email-uri suplimentare (separați prin virgulă sau spațiu)
                        </span>
                      </label>
                      <textarea
                        id="emailAddresses"
                        value={emailAddresses}
                        onChange={(e) => setEmailAddresses(e.target.value)}
                        placeholder="exemplu@email.ro, alt@email.ro"
                        rows={3}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={includeClientEmails}
                          onChange={(e) => setIncludeClientEmails(e.target.checked)}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">
                          Include email-urile clientului
                        </span>
                      </label>
                      {includeClientEmails && clientEmailsDisplay && (
                        <div className="client-emails-display">
                          <Mail className="w-4 h-4" />
                          <span>{clientEmailsDisplay}</span>
                        </div>
                      )}
                      {includeClientEmails && !clientEmailsDisplay && (
                        <div className="client-emails-warning">
                          <AlertCircle className="w-4 h-4" />
                          <span>Clientul nu are email-uri configurate</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Anulează
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading
                    ? 'Se procesează...'
                    : (actionType === 'create-new' ? 'Generează Notificare' : 'Trimite Email')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .termination-notice-actions {
          margin: 20px 0;
        }

        .alert {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 16px;
        }

        .alert-success {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert button {
          margin-left: auto;
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .mb-4 {
          margin-bottom: 1rem;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #545b62;
        }

        .act-list-container {
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
            border: 1px solid #e5e7eb;
            padding: 24px;
            margin-top: 16px;
        }

        .list-heading {
            font-size: 18px;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 16px;
        }

        .space-y-3 > * + * {
            margin-top: 0.75rem;
        }

        .act-list-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px;
            background-color: #f9fafb;
            border-radius: 6px;
        }

        .act-number {
            font-size: 14px;
            font-weight: 500;
            color: #1f2937;
        }

        .act-date {
            font-size: 14px;
            color: #6b7280;
        }

        .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 2.5px 10px;
            border-radius: 9999px;
            font-size: 12px;
            font-weight: 500;
        }

        .status-badge .w-3 {
            width: 12px;
            height: 12px;
            margin-right: 4px;
        }

        .btn-link {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #10b981;
            transition: color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .btn-link:hover {
            color: #059669;
        }

        .btn-link-primary {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #007bff;
            transition: color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .btn-link-primary:hover {
            color: #0056b3;
        }

        .text-secondary-500 {
            color: #6c757d;
        }

        .text-secondary-500:hover {
             color: #545b62;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #dee2e6;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 18px;
        }

        .modal-header button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          opacity: 0.7;
        }

        .modal-header button:hover {
          opacity: 1;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #dee2e6;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          padding: 10px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
        }

        .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
        }

        .hint {
          display: block;
          font-size: 12px;
          color: #6c757d;
          margin-top: 4px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          margin-bottom: 8px;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .checkbox-text {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }

        .client-emails-display {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          background-color: #e0f2fe;
          border: 1px solid #bae6fd;
          border-radius: 6px;
          margin-top: 8px;
          font-size: 13px;
          color: #075985;
        }

        .client-emails-display .w-4 {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .client-emails-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          background-color: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 6px;
          margin-top: 8px;
          font-size: 13px;
          color: #92400e;
        }

        .client-emails-warning .w-4 {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default TerminationNoticeActions;